import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  ogImage?: string
  ogType?: string
  canonicalUrl?: string
  structuredData?: object
  noindex?: boolean
}

const DEFAULT_TITLE = 'Clawbiz — Personal AI Assistant'
const DEFAULT_DESCRIPTION = 'Clawbiz — The AI that actually does things. Your personal assistant on any platform.'
const DEFAULT_OG_IMAGE = 'https://clawbiz.ai/og-image.png'
const DEFAULT_SITE_URL = 'https://clawbiz.ai'
const DEFAULT_KEYWORDS = 'Clawbiz, AI assistant, personal AI, self-hosted AI, WhatsApp AI, Telegram bot, Discord bot'
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://clawbiz.ai'

export function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  canonicalUrl,
  structuredData,
  noindex = false,
}: SEOProps) {
  const fullTitle = title.includes('Clawbiz') ? title : `${title} | Clawbiz`
  const siteUrl = typeof window !== 'undefined' && window.location.origin ? window.location.origin : DEFAULT_SITE_URL
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`
  const canonical = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : siteUrl)

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical || SITE_URL} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Clawbiz" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical || SITE_URL} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />

      <meta name="author" content="Clawbiz" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  )
}
